// Temporary, until the React #185 fix is confirmed (README: "React #185 lane
// diagnostics"). Reads react-dom 19.2 internals so a `#185` Bugsnag event names the
// component that keeps work pending across commits. The shapes below are the subset
// this reads; the test runs against a real root, so a React bump that renames a field
// fails there.
interface Update {
	lane: number;
	next: Update | null;
}

interface Hook {
	queue: { pending: Update | null } | null;
	baseQueue: Update | null;
	next: Hook | null;
}

interface ClassUpdateQueue {
	shared: { pending: Update | null };
	firstBaseUpdate: Update | null;
}

interface Fiber {
	tag: number;
	type: unknown;
	lanes: number;
	child: Fiber | null;
	sibling: Fiber | null;
	return: Fiber | null;
	stateNode: unknown;
	memoizedState: unknown;
	updateQueue: unknown;
}

interface FiberRoot {
	current: Fiber;
	pendingLanes: number;
	suspendedLanes: number;
	pingedLanes: number;
	expiredLanes: number;
	entangledLanes: number;
	callbackPriority: number;
	callbackNode: unknown;
	cancelPendingCommit: unknown;
	timeoutHandle: unknown;
}

// Sync | InputContinuous | Default: a commit that leaves any of these pending counts
// towards React 19's nested-update limit.
const NESTED_UPDATE_LANES = 2 | 8 | 32;
const MAX_FIBERS_PER_ROOT = 10;
const FINGERPRINT_LENGTH = 120;
const MAX_MUI_CLASSES = 3;
const CONTAINER_KEY_PREFIX = '__reactContainer$';
const NO_TIMEOUT = -1;

const FunctionComponent = 0;
const ClassComponent = 1;
const HostRoot = 3;
const HostComponent = 5;
const ForwardRef = 11;
const SimpleMemoComponent = 15;

const laneUnion = (first: Update | null): number => {
	let lanes = 0;
	for (let update = first; update; update = update.next === first ? null : update.next) {
		lanes |= update.lane;
	}
	return lanes;
};

const queuedLanes = (fiber: Fiber) => {
	const queues: { hook: number | 'updateQueue'; pending: number; base: number }[] = [];
	if (
		fiber.tag === FunctionComponent ||
		fiber.tag === ForwardRef ||
		fiber.tag === SimpleMemoComponent
	) {
		let index = 0;
		for (let hook = fiber.memoizedState as Hook | null; hook; hook = hook.next, index++) {
			const pending = laneUnion(hook.queue?.pending ?? null);
			const base = laneUnion(hook.baseQueue);
			if (pending || base) queues.push({ hook: index, pending, base });
		}
	} else if ((fiber.tag === ClassComponent || fiber.tag === HostRoot) && fiber.updateQueue) {
		const queue = fiber.updateQueue as ClassUpdateQueue;
		const pending = laneUnion(queue.shared.pending);
		const base = laneUnion(queue.firstBaseUpdate);
		if (pending || base) queues.push({ hook: 'updateQueue', pending, base });
	}
	return queues;
};

const componentFunction = (type: unknown): ((...args: never[]) => unknown) | null => {
	if (typeof type === 'function') return type as (...args: never[]) => unknown;
	if (type && typeof type === 'object') {
		const { render, type: inner } = type as { render?: unknown; type?: unknown };
		return componentFunction(render ?? inner);
	}
	return null;
};

const nameOf = (type: unknown, fn: ((...args: never[]) => unknown) | null): string | null => {
	if (typeof type === 'string') return type;
	const { displayName } = (type ?? {}) as { displayName?: unknown };
	if (typeof displayName === 'string') return displayName;
	return fn ? (fn as { displayName?: string }).displayName || fn.name || null : null;
};

// The component's own first DOM node, else the DOM node it renders into.
const nearestHostElement = (fiber: Fiber): Element | null => {
	for (let node: Fiber | null = fiber; node; node = node.child) {
		if (node.tag === HostComponent) return node.stateNode as Element;
	}
	for (let node = fiber.return; node; node = node.return) {
		if (node.tag === HostComponent) return node.stateNode as Element;
	}
	return null;
};

// Some test ids end in a record id (`invoice-download-button-${invoice.id}`): any
// segment with a digit or an `@` is replaced, so only the static prefix is sent.
const redactTestId = (testId: string | null) =>
	testId &&
	testId
		.split('-')
		.map((segment) => (/[\d@]/.test(segment) ? '#' : segment))
		.join('-');

const describeFiber = (fiber: Fiber) => {
	const fn = componentFunction(fiber.type);
	const host = nearestHostElement(fiber);
	return {
		tag: fiber.tag,
		name: nameOf(fiber.type, fn),
		lanes: fiber.lanes,
		fingerprint: fn ? Function.prototype.toString.call(fn).slice(0, FINGERPRINT_LENGTH) : null,
		queues: queuedLanes(fiber),
		host: host && {
			tag: host.tagName.toLowerCase(),
			testId: redactTestId(host.getAttribute('data-testid')),
			muiClasses: Array.from(host.classList)
				.filter((name) => name.startsWith('Mui'))
				.slice(0, MAX_MUI_CLASSES),
		},
	};
};

// Walks the whole tree, not just the `childLanes` path: an update enqueued since the
// last render (including the one that throws `#185`) has only marked its own fiber.
const describeRoot = (root: FiberRoot) => {
	const fibers: ReturnType<typeof describeFiber>[] = [];
	let fibersCapped = false;
	const stack = [root.current];
	for (let fiber = stack.pop(); fiber; fiber = stack.pop()) {
		if (fiber.lanes & NESTED_UPDATE_LANES) {
			if (fibers.length === MAX_FIBERS_PER_ROOT) {
				fibersCapped = true;
				break;
			}
			fibers.push(describeFiber(fiber));
		}
		if (fiber.sibling) stack.push(fiber.sibling);
		if (fiber.child) stack.push(fiber.child);
	}
	return {
		pendingLanes: root.pendingLanes,
		suspendedLanes: root.suspendedLanes,
		pingedLanes: root.pingedLanes,
		expiredLanes: root.expiredLanes,
		entangledLanes: root.entangledLanes,
		callbackPriority: root.callbackPriority,
		hasCallbackNode: root.callbackNode !== null,
		hasCancelPendingCommit: root.cancelPendingCommit !== null,
		hasTimeoutHandle: root.timeoutHandle !== NO_TIMEOUT,
		fibers,
		fibersCapped,
	};
};

type RootDiagnostics = { container: string } & (
	| ReturnType<typeof describeRoot>
	| { error: string }
);

/**
 * Snapshot of every React root mounted directly under `document.body`: its lane
 * bookkeeping and the fibers holding Sync/InputContinuous/Default work. Reads no
 * props, state values, text or form values, and never throws — a root it cannot
 * read is reported as `{ container, error }`.
 */
export function collectReactLaneDiagnostics() {
	return {
		roots: Array.from(document.body?.children ?? []).flatMap((container): RootDiagnostics[] => {
			const key = Object.keys(container).find((name) => name.startsWith(CONTAINER_KEY_PREFIX));
			const hostRootFiber = key && (container as unknown as Record<string, unknown>)[key];
			if (!hostRootFiber) return [];
			try {
				const root = (hostRootFiber as { stateNode: FiberRoot }).stateNode;
				return [{ container: container.id, ...describeRoot(root) }];
			} catch (error) {
				return [{ container: container.id, error: String(error) }];
			}
		}),
	};
}
